import { defineStore } from 'pinia'

import {
	fetchApiGetExportTask,
	fetchApiStartExportFiles
} from '~/portal/api/apiChecklistExport.ts'
import { useSnackbar } from '~/portal/composables'

type ExportStatus = 'IDLE' | 'STARTED' | 'COMPLETED' | 'FAILED' | 'TIMEOUT'

type ExportTask = {
	taskId: string
	projectId: number
	checklistId: number
	status: ExportStatus
	startedAt: number
	url?: string
	lastError?: string
}

const POLL_INTERVAL_MS = 5000
const EXPORT_TIMEOUT_MS = 300_000

function keyOf(projectId: number, checklistId: number) {
	return `${projectId}:${checklistId}`
}

function openInNewTab(url: string) {
	if (typeof window === 'undefined') return
	const win = window.open(url, '_blank', 'noopener')
	if (!win) {
		console.warn('Popup blockiert beim Öffnen der Export-URL.')
	}
}

let unloadGuardOn = false
const beforeUnloadHandler = (e: BeforeUnloadEvent) => {
	e.preventDefault()
	e.returnValue = ''
}
function enableUnloadGuard() {
	if (unloadGuardOn || typeof window === 'undefined') return
	window.addEventListener('beforeunload', beforeUnloadHandler)
	unloadGuardOn = true
}
function disableUnloadGuard() {
	if (!unloadGuardOn || typeof window === 'undefined') return
	window.removeEventListener('beforeunload', beforeUnloadHandler)
	unloadGuardOn = false
}

export const useExportFilesStore = defineStore('exportFiles', {
	state: () => ({
		tasksById: {} as Record<string, ExportTask>,
		indexByKey: {} as Record<string, Set<string>>,
		pollTimer: null as number | null,
		authToken: null as string | null,
		isPollingNow: false as boolean
	}),
	getters: {
		pendingTasks(state): ExportTask[] {
			return Object.values(state.tasksById).filter(t => t.status === 'STARTED')
		},
		getTasksFor:
			state =>
			(projectId: number, checklistId: number): ExportTask[] => {
				const ids = state.indexByKey[keyOf(projectId, checklistId)]
				if (!ids) return []
				return Array.from(ids)
					.map(id => state.tasksById[id])
					.filter(Boolean)
			},
		getTask() {
			return (
				projectId: number,
				checklistId: number
			): ExportTask | undefined => {
				const list = this.getTasksFor(projectId, checklistId)
				if (list.length === 0) return undefined
				return [...list].sort((a, b) => b.startedAt - a.startedAt)[0]
			}
		},
		isExportingFor() {
			return (projectId: number, checklistId: number): boolean =>
				this.getTasksFor(projectId, checklistId).some(
					t => t.status === 'STARTED'
				)
		}
	},
	actions: {
		setToken(token: string | null) {
			this.authToken = token
		},

		async startExport(
			projectId: number,
			checklistId: number,
			opts?: { token?: string | null }
		) {
			const { showSnackbar } = useSnackbar()
			const token = opts?.token ?? this.authToken
			if (!token) {
				showSnackbar('Nicht authentifiziert', 'cp-base-red')
				return
			}

			try {
				const start = await fetchApiStartExportFiles({
					token,
					projectId,
					checklistId
				})
				if (!start?.task_id) {
					showSnackbar('Export konnte nicht gestartet werden', 'cp-base-red')
					return
				}

				const task: ExportTask = {
					taskId: start.task_id,
					projectId,
					checklistId,
					status: 'STARTED',
					startedAt: Date.now()
				}

				this.tasksById[task.taskId] = task
				const k = keyOf(projectId, checklistId)
				if (!this.indexByKey[k]) this.indexByKey[k] = new Set<string>()
				this.indexByKey[k].add(task.taskId)

				this.ensurePolling()
				showSnackbar(
					'Wir bereiten Ihre Dateien vor. Bitte verlassen Sie die Website nicht und laden Sie die Seite nicht neu, bis der Export abgeschlossen ist.'
				)
			} catch {
				const { showSnackbar } = useSnackbar()
				showSnackbar('Export konnte nicht gestartet werden', 'cp-base-red')
			}
		},

		ensurePolling() {
			if (this.pollTimer || typeof window === 'undefined') return
			this.pollTimer = window.setInterval(
				() => this.pollOnce(),
				POLL_INTERVAL_MS
			)
			enableUnloadGuard()
		},

		stopPolling() {
			if (this.pollTimer) {
				clearInterval(this.pollTimer)
				this.pollTimer = null
			}
			if (this.pendingTasks.length === 0) disableUnloadGuard()
		},

		async pollOnce() {
			const { showSnackbar } = useSnackbar()
			const token = this.authToken
			if (!token) return
			if (this.isPollingNow) return
			this.isPollingNow = true

			try {
				const now = Date.now()
				const tasks = this.pendingTasks
				if (tasks.length === 0) {
					this.stopPolling()
					return
				}

				await Promise.allSettled(
					tasks.map(async t => {
						if (now - t.startedAt > EXPORT_TIMEOUT_MS) {
							this.tasksById[t.taskId].status = 'TIMEOUT'
							showSnackbar(
								'Zeitüberschreitung beim Export. Bitte erneut versuchen.',
								'cp-base-red'
							)
							return
						}

						try {
							const status = await fetchApiGetExportTask({
								token,
								projectId: t.projectId,
								checklistId: t.checklistId,
								taskId: t.taskId
							})

							if (!status) return
							if (status.status === 'STARTED') return

							if (status.status === 'FAILED') {
								this.tasksById[t.taskId].status = 'FAILED'
								showSnackbar('Export fehlgeschlagen', 'cp-base-red')
								return
							}

							if (status.status === 'COMPLETED') {
								const fileUrl = (status as any).file_url ?? (status as any).url
								if (!fileUrl) {
									this.tasksById[t.taskId].status = 'FAILED'
									showSnackbar(
										'Export fehlgeschlagen: Keine Datei-URL',
										'cp-base-red'
									)
									return
								}
								this.tasksById[t.taskId].status = 'COMPLETED'
								this.tasksById[t.taskId].url = fileUrl
								openInNewTab(fileUrl)
								showSnackbar('Export abgeschlossen')
								setTimeout(() => this.removeTask(t.taskId), 10_000)
							}
						} catch {
							this.tasksById[t.taskId].status = 'FAILED'
							showSnackbar(
								'Exportstatus konnte nicht abgerufen werden',
								'cp-base-red'
							)
						}
					})
				)
			} finally {
				this.isPollingNow = false
				if (this.pendingTasks.length === 0) this.stopPolling()
			}
		},

		removeTask(taskId: string) {
			const task = this.tasksById[taskId]
			if (!task) return
			const k = keyOf(task.projectId, task.checklistId)
			const set = this.indexByKey[k]
			if (set) {
				set.delete(taskId)
				if (set.size === 0) delete this.indexByKey[k]
			}
			delete this.tasksById[taskId]
		}
	}
})
